import "./../views/_menupage.css";
import { JitsiMeeting } from "@jitsi/react-sdk";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { useBrand } from "./../brand";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
// 預設同源的 /wbo/boards —— 內網部署一定是 nginx 把白板掛在同一個來源之下。
// 沒有預設值的話，缺 .env 時會變成字串 "undefined/房名"，白板直接開不起來。
const WHITEBOARD_BASE_URL =
  process.env.REACT_APP_WHITEBOARD_BASE_URL || "/wbo/boards";
// Jitsi 位址一律在執行時決定，不在編譯時寫死 —— 否則換機器、換埠都要重新 build。
// 順序：.env 指定 > 後端 /api/config > 目前網址的主機名 + 8443。
const BUILD_TIME_DOMAIN = process.env.REACT_APP_JITSI_DOMAIN || null;

// 預設的 Jitsi 掛載路徑。與 nginx 的 location /jitsi/ 和
// deploy/jitsi/.env 的 PUBLIC_URL 一致。
const DEFAULT_JITSI_PATH = "/jitsi";

const sameOrigin = (path) =>
  `${window.location.host}${path}`.replace(/\/+$/, "");

const resolveJitsiDomain = (cfg) => {
  if (BUILD_TIME_DOMAIN) return BUILD_TIME_DOMAIN;
  // 同源子路徑優先。用 location.host（含埠）＋ 路徑，
  // 這樣不論使用者是用 IP 還是主機名進來都對得上，也不必重新編譯。
  if (cfg && cfg.jitsiPath) return sameOrigin(cfg.jitsiPath);
  // 只有後端明確指定「Jitsi 在別台」時才走獨立來源
  if (cfg && cfg.jitsiDomain) return cfg.jitsiDomain;
  if (cfg && cfg.jitsiPort) return `${window.location.hostname}:${cfg.jitsiPort}`;
  // 拿不到設定時的退路。這裡一定要用同源路徑：
  // Jitsi 容器只綁 127.0.0.1，對外沒有 8443，退回那個埠必定是一片全黑。
  return sameOrigin(DEFAULT_JITSI_PATH);
};

// 自架 Jitsi 若尚未開啟 ENABLE_AUTH=1 / AUTH_TYPE=jwt，prosody 只接受 ANONYMOUS，
// 這時候送 JWT 反而會被拒絕，所以做成開關。
// 預設「開啟」，要關才設成 false。
//
// 原本寫 === "true"，也就是沒有 .env 就等於關掉。但 update.ps1 產生的
// jitsi\.env 一律是 ENABLE_AUTH=1 + AUTH_TYPE=jwt —— 伺服器永遠要求 JWT。
// 前端不送的話，Jitsi 會跳出「需要驗證」要使用者輸入帳號密碼。
// 而 .env 不進版控，換一台機器 build 就會無聲無息關掉，症狀還完全看不出
// 是前端造成的（伺服器端只會說 "token required"）。
// 所以改成 opt-out：預設跟伺服器一致，要關再明講。
const JWT_ENABLED = process.env.REACT_APP_JITSI_JWT_ENABLED !== "false";

const MeetingRoom = () => {
  const { id } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  // Jitsi 內部一律把房名正規化成小寫，白板網址與 JWT 都要跟著小寫，否則對不上。
  const roomName = (id || "").toLowerCase();

  const companyId = params.get("company") || "";
  const role = params.get("role") || "guest";
  const isHost = role === "host";
  const displayName = params.get("name") || (isHost ? "主持人" : "參與者");

  const brand = useBrand();
  const navigate = useNavigate();
  const apiRef = useRef(null);
  const participantsRef = useRef([]);

  const [jwtToken, setJwtToken] = useState(null);
  const [tokenReady, setTokenReady] = useState(!JWT_ENABLED);
  const [jitsiDomain, setJitsiDomain] = useState(BUILD_TIME_DOMAIN);
  const [toggleWBO, setToggleWBO] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [selectedParticipantId, setSelectedParticipantId] = useState("");

  // ── 取得執行時設定（Jitsi 位址）────────────────────────────────
  useEffect(() => {
    if (BUILD_TIME_DOMAIN) return;   // .env 有指定就不必問後端
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/config`)
      .then((res) => res.json())
      .then((cfg) => {
        if (!cancelled) setJitsiDomain(resolveJitsiDomain(cfg));
      })
      .catch(() => {
        // 後端拿不到就退回預設埠，至少讓同機部署的情況能動
        if (!cancelled) setJitsiDomain(resolveJitsiDomain(null));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── 取得 JWT（僅在後端有開 token 驗證時）──────────────────────
  useEffect(() => {
    if (!JWT_ENABLED) {
      setTokenReady(true);
      return;
    }

    const url =
      `${API_BASE_URL}/api/token?room=${encodeURIComponent(roomName)}` +
      `&role=${encodeURIComponent(role)}` +
      `&displayName=${encodeURIComponent(displayName)}` +
      `&company=${encodeURIComponent(companyId)}`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          alert(`❌ 取得會議憑證失敗：${data.error}`);
          navigate("/");
          return;
        }
        setJwtToken(data.jwt);
        setTokenReady(true);
      })
      .catch((err) => {
        console.error("獲取 JWT 失敗", err);
        alert("Token 伺服器連線失敗");
        navigate("/");
      });
  }, [roomName, role, companyId, displayName, navigate]);

  // ── Jitsi 設定 ────────────────────────────────────────────────
  const appConfig = {
    disableDominantSpeakerIndicator: true,
    disableTileView: true,
    startWithAudioMuted: !isHost,
    startWithVideoMuted: false,
    disableModeratorIndicator: false,
    startAudioOnly: false,
    doNotFlipLocalVideo: true,
    channelLastN: 1,

    // 內網部署：不要對外抓 gravatar / analytics / 公用 STUN，否則載入會卡住。
    disableThirdPartyRequests: true,
    analytics: { disabled: true },
    // 關掉 P2P：內網沒有公用 STUN，兩人通話走 P2P 容易協商失敗，
    // 一律走 JVB 比較可預期（多人本來就一定走 JVB）。
    p2p: { enabled: false },
    prejoinConfig: { enabled: false },

    // 手機瀏覽器開會議時，Jitsi 預設會先跳一頁「您想如何加入此會議？」，
    // 上面是 Jitsi 自己的 logo 和「Jitsi Meet」字樣，還推你去 App Store。
    // 內網部署沒有意義（那個 App 連不到我們的伺服器），而且蓋掉我們的品牌。
    // 伺服器端的 DISABLE_DEEP_LINKING 也設了，這裡是雙保險。
    disableDeepLinking: true,

    // 新版 Jitsi 的工具列設定已從 interfaceConfig 搬到 config。
    toolbarButtons: [
      "microphone",
      "camera",
      "settings",
      "participants-pane",
      "hangup",
    ],
  };

  // 會議畫面裡的品牌。因為 Jitsi 現在掛在同一個來源，
  // /brand/logo.png 這種相對路徑在它的 iframe 裡也解析得到。
  const showOurLogo = Boolean(brand.logo) && brand.showJitsiWatermark !== false;

  const interfaceConfig = {
    // 舊版 Jitsi 相容用；新版讀上面的 toolbarButtons。
    TOOLBAR_BUTTONS: ["microphone", "camera", "overflowmenu", "hangup"],
    SETTINGS_SECTIONS: ["devices", "language", "moderator"],
    APP_NAME: brand.appName,
    PROVIDER_NAME: brand.appName,
    // 用我們自己的 LOGO 取代 Jitsi 的浮水印
    SHOW_JITSI_WATERMARK: showOurLogo,
    JITSI_WATERMARK_LINK: "",
    DEFAULT_LOGO_URL: brand.logo || undefined,
    DEFAULT_WELCOME_PAGE_LOGO_URL: brand.logo || undefined,
    SHOW_WATERMARK_FOR_GUESTS: showOurLogo,
    SHOW_BRAND_WATERMARK: false,
    SHOW_POWERED_BY: false,
    DISABLE_FOCUS_INDICATOR: true,
    DISABLE_DOMINANT_SPEAKER_INDICATOR: true,
    DISABLE_TRANSCRIPTION_SUBTITLES: true,
    DISABLE_VIDEO_BACKGROUND: true,
    FILM_STRIP_MAX_HEIGHT: 100,
    TILE_VIEW_MAX_COLUMNS: 1,
    DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
    DISABLE_PRESENCE_STATUS: true,
    DISABLE_RINGING: true,
  };

  // ── 主持人固定選用特定攝影機 (如 "ORMASTER MULTI VIEW") ─────────
  const targetCameraName = params.get("camera") || "ORMASTER MULTI VIEW";

  const selectPreferredCamera = async (api) => {
    if (!api || typeof api.getAvailableDevices !== "function") return;
    try {
      const devices = await api.getAvailableDevices();
      const videoDevices = devices?.videoInput || [];

      // 優先尋找名稱包含目標關鍵字的攝影機（不分大小寫）
      const preferred = videoDevices.find((device) =>
        (device.label || "").toUpperCase().includes(targetCameraName.toUpperCase())
      );

      if (preferred) {
        console.log(`[MeetingRoom] 主持人自動選用攝影機: ${preferred.label} (${preferred.deviceId})`);
        if (typeof api.setVideoInputDevice === "function") {
          api.setVideoInputDevice(preferred.label, preferred.deviceId);
        } else {
          api.executeCommand("setVideoInputDevice", preferred.label, preferred.deviceId);
        }
      }
    } catch (err) {
      console.warn("[MeetingRoom] 自動切換攝影機裝置失敗:", err);
    }
  };

  const handleApiReady = (api) => {
    apiRef.current = api;

    // ── 房間心跳與在線狀態回報 ──────────────────────────────
    let heartbeatTimer = null;
    const sendHeartbeat = () => {
      if (!roomName) return;
      const count = (apiRef.current?.getParticipantsInfo()?.length || 0) + 1;
      fetch(`${API_BASE_URL}/api/rooms/${encodeURIComponent(roomName)}/heartbeat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ count }),
      }).catch(() => {});
    };

    const sendLeave = () => {
      if (!roomName) return;
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
      try {
        navigator.sendBeacon?.(`${API_BASE_URL}/api/rooms/${encodeURIComponent(roomName)}/leave`);
      } catch (e) {}
    };

    api.addEventListener("videoConferenceLeft", () => {
      sendLeave();
      navigate("/");
    });

    api.addEventListener("videoConferenceJoined", (event) => {
      api.executeCommand("setTileView", false);
      sendHeartbeat();
      if (!heartbeatTimer) {
        heartbeatTimer = setInterval(sendHeartbeat, 10000);
      }

      if (isHost) {
        // 主持人釘選自己並開 followMe，其他人的畫面會自動跟著釘到主持人，
        // 不需要再輪詢比對 displayName（舊版那個比對永遠不成立）。
        api.executeCommand("pinParticipant", event.id);
        api.executeCommand("setFollowMe", true);

        // 主持人自動選用固定攝影機 ("ORMASTER MULTI VIEW")
        selectPreferredCamera(api);
        setTimeout(() => selectPreferredCamera(api), 800);
      }
    });

    window.addEventListener("beforeunload", sendLeave);

    // 裝置清單更新時，若為主持人亦自動切換至固定攝影機
    api.addEventListener("deviceListChanged", () => {
      if (isHost) {
        selectPreferredCamera(api);
      }
    });

    api.addEventListener("endpointTextMessageReceived", (res) => {
      const { type, action } = res?.data?.eventData?.text || {};
      if (type === "whiteboardControl") {
        setToggleWBO(action === "start");
      }
    });
  };

  // ── 參與者清單輪詢 ────────────────────────────────────────────
  const updateParticipants = useCallback(() => {
    if (!apiRef.current) return;
    const info = apiRef.current.getParticipantsInfo() || [];
    const filtered = info.filter(
      (p) => !(p.formattedDisplayName || "").includes("me")
    );
    if (JSON.stringify(filtered) !== JSON.stringify(participantsRef.current)) {
      participantsRef.current = filtered;
      setParticipants(filtered);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(updateParticipants, 5000);
    return () => clearInterval(interval);
  }, [updateParticipants]);

  // ── 主持人控制 ────────────────────────────────────────────────
  const muteEveryone = () => apiRef.current?.executeCommand("muteEveryone");

  const kickParticipant = () => {
    if (!selectedParticipantId || !apiRef.current) return;
    apiRef.current.executeCommand("kickParticipant", selectedParticipantId);
    setParticipants((prev) =>
      prev.filter((p) => p.participantId !== selectedParticipantId)
    );
    setSelectedParticipantId("");
  };

  const selectedParticipantName =
    participants.find((p) => p.participantId === selectedParticipantId)
      ?.displayName || "參與者";

  // ── 白板 ──────────────────────────────────────────────────────
  const toggleWhiteboard = () => {
    const next = !toggleWBO;
    apiRef.current?.executeCommand("sendEndpointTextMessage", "", {
      type: "whiteboardControl",
      action: next ? "start" : "stop",
    });
    setToggleWBO(next);
  };

  if (!tokenReady || !jitsiDomain) {
    return <div style={{ color: "white", padding: "2rem" }}>會議載入中…</div>;
  }

  return (
    <div className="container-iframe">
      {isHost && (
        <div className="whiteboard-btn-container">
          <button className="control-btn" onClick={muteEveryone}>
            🔇 靜音所有人
          </button>

          <select
            className="control-btn"
            value={selectedParticipantId}
            onChange={(e) => setSelectedParticipantId(e.target.value)}
          >
            <option value="">選擇參與者</option>
            {participants.map((p) => (
              <option key={p.participantId} value={p.participantId}>
                {p.displayName || p.formattedDisplayName || p.participantId}
              </option>
            ))}
          </select>

          <button
            className="control-btn"
            onClick={kickParticipant}
            disabled={!selectedParticipantId}
          >
            踢掉 {selectedParticipantName}
          </button>
        </div>
      )}

      <button
        className="whiteboard-btn"
        onClick={toggleWhiteboard}
        style={{ marginBottom: "10px" }}
      >
        白板
      </button>

      <JitsiMeeting
        domain={jitsiDomain}
        roomName={roomName}
        jwt={JWT_ENABLED ? jwtToken : undefined}
        configOverwrite={appConfig}
        userInfo={{ displayName }}
        interfaceConfigOverwrite={interfaceConfig}
        lang="zhTW"
        onApiReady={handleApiReady}
      />

      {toggleWBO && (
        <div className="whiteboard-container">
          <iframe
            src={`${WHITEBOARD_BASE_URL}/${roomName}`}
            title="Whiteboard"
            className="whiteboard-iframe"
          ></iframe>
        </div>
      )}
    </div>
  );
};

export default MeetingRoom;
